'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PlanBillingPeriod } from '@/core/domain';
import { useToast } from '@/core/presentation/components/Toast';
import { useAuth } from '@/features/authentication/presentation/context/AuthContext';
import { ROUTES } from '@/app/routes';
import { useSubscription } from '@/features/app/subscription/presentation/context/SubscriptionContext';
import { usePlans } from '@/features/plans/presentation/context/PlansContext';
import { usePayments } from '@/features/payments/presentation/context/PaymentsContext';
import { useChoosePlanScreenData } from '@/features/plans/presentation/composables/hooks/useChoosePlanScreenData';
import { buildPremiumPlanCardDisplay } from '@/features/plans/presentation/utils/choose-plan-dual-layout';
import {
  CHOOSE_PLAN_CHECKOUT_LOADING_MIN_MS,
  CHOOSE_PLAN_EXIT_FOOTER_LABEL,
  CHOOSE_PLAN_EXIT_LOADING_LABEL,
  CHOOSE_PLAN_FLOW_ACTIVE_STEP,
  CHOOSE_PLAN_SCREEN_TITLE,
  CHOOSE_PLAN_CHECKOUT_LOADING_LABEL,
  CHOOSE_PLAN_SUBSCRIBE_CTA,
  CHOOSE_PLAN_TOAST_CHECKOUT_FAILED,
  CHOOSE_PLAN_TOAST_INTERNAL_FREE_FAILED,
  CHOOSE_PLAN_TOAST_INTERNAL_FREE_OK,
  CHOOSE_PLAN_TOAST_PLANS_LOAD_FAILED,
} from '@/features/plans/presentation/composables/constants/choose-plan-screen.constants';
import type { ChoosePlanViewProps } from '@/features/plans/presentation/components/ChoosePlanView';

export type ChoosePlanBridgeResult = {
  layout: { title: string };
  view: ChoosePlanViewProps;
  exitFooter: {
    loading: boolean;
    loadingLabel: string;
    label: string;
    onExit: () => void | Promise<void>;
  };
};

export function useChoosePlanBridge(): ChoosePlanBridgeResult {
  const navigate = useNavigate();
  const toast = useToast();
  const { state: plansState, events: plansEvents } = usePlans();
  const { events: paymentsEvents } = usePayments();
  const { state: authState, events: authEvents } = useAuth();
  const { events: subscriptionEvents } = useSubscription();

  const [checkoutLoadingPlanId, setCheckoutLoadingPlanId] = useState<string | null>(null);
  const [exitLoading, setExitLoading] = useState(false);
  const [premiumTab, setPremiumTab] = useState<PlanBillingPeriod>('semiannual');

  const handleExitToLogin = useCallback(async () => {
    setExitLoading(true);
    try {
      await authEvents.logout();
      navigate(ROUTES.LOGIN, { replace: true });
    } finally {
      setExitLoading(false);
    }
  }, [authEvents, navigate]);

  useEffect(() => {
    void plansEvents.refresh();
  }, [plansEvents]);

  useEffect(() => {
    if (!plansState.error) return;
    toast.error(CHOOSE_PLAN_TOAST_PLANS_LOAD_FAILED);
    plansEvents.clearError();
  }, [plansState.error, plansEvents, toast]);

  const screenData = useChoosePlanScreenData({
    plans: plansState.plans,
    loading: plansState.loading,
    error: plansState.error,
  });

  const { dualModel } = screenData;

  useEffect(() => {
    if (dualModel.premiumByPeriod[premiumTab] != null) return;
    const order: readonly PlanBillingPeriod[] = ['semiannual', 'monthly', 'annual'];
    const next = order.find((p) => dualModel.premiumByPeriod[p] != null);
    if (next != null && next !== premiumTab) {
      setPremiumTab(next);
    }
  }, [dualModel, premiumTab]);

  const premiumCard = useMemo(() => {
    const ref = dualModel.premiumByPeriod[premiumTab];
    if (!ref) return null;
    return buildPremiumPlanCardDisplay(ref, dualModel.monthlyBaselineCents);
  }, [dualModel, premiumTab]);

  const onPremiumTabChange = useCallback((period: PlanBillingPeriod) => {
    setPremiumTab(period);
  }, []);

  const handleSubscribe = useCallback(
    async (planId: string) => {
      const plan = plansState.plans.find((p) => p.id === planId);
      if (plan?.kind === 'free') {
        setCheckoutLoadingPlanId(planId);
        const startedAt = Date.now();
        try {
          const result = await paymentsEvents.activateFreePlan(planId);
          if (result.success) {
            const uid = authState.user?.uid;
            if (uid) {
              await subscriptionEvents.getSubscriptionByUserId(uid);
            }
            toast.success(CHOOSE_PLAN_TOAST_INTERNAL_FREE_OK);
            navigate(ROUTES.APP, { replace: true });
            return;
          }
          toast.error(result.error || CHOOSE_PLAN_TOAST_INTERNAL_FREE_FAILED);
          paymentsEvents.clearError();
        } catch {
          toast.error(CHOOSE_PLAN_TOAST_INTERNAL_FREE_FAILED);
          paymentsEvents.clearError();
        } finally {
          const elapsed = Date.now() - startedAt;
          const remaining = Math.max(0, CHOOSE_PLAN_CHECKOUT_LOADING_MIN_MS - elapsed);
          if (remaining > 0) {
            await new Promise((resolve) => setTimeout(resolve, remaining));
          }
          setCheckoutLoadingPlanId(null);
        }
        return;
      }

      setCheckoutLoadingPlanId(planId);
      const startedAt = Date.now();
      let openedCheckout = false;
      try {
        const result = await paymentsEvents.createSubscription(planId, premiumTab);
        if (result.success) {
          openedCheckout = true;
          window.location.assign(result.data.url);
          return;
        }
        toast.error(result.error || CHOOSE_PLAN_TOAST_CHECKOUT_FAILED);
        paymentsEvents.clearError();
      } catch {
        toast.error(CHOOSE_PLAN_TOAST_CHECKOUT_FAILED);
        paymentsEvents.clearError();
      } finally {
        if (!openedCheckout) {
          const elapsed = Date.now() - startedAt;
          const remaining = Math.max(0, CHOOSE_PLAN_CHECKOUT_LOADING_MIN_MS - elapsed);
          if (remaining > 0) {
            await new Promise((resolve) => setTimeout(resolve, remaining));
          }
        }
        setCheckoutLoadingPlanId(null);
      }
    },
    [
      authState.user?.uid,
      navigate,
      paymentsEvents,
      plansState.plans,
      premiumTab,
      subscriptionEvents,
      toast,
    ]
  );

  const view = useMemo(
    (): ChoosePlanViewProps => ({
      activeStep: CHOOSE_PLAN_FLOW_ACTIVE_STEP,
      dualModel,
      premiumTab,
      onPremiumTabChange,
      premiumCard,
      showMonthlyHint: screenData.showMonthlyHint,
      showListLoading: screenData.showListLoading,
      showEmptyState: screenData.showEmptyState,
      onSubscribe: handleSubscribe,
      checkoutLoadingPlanId,
      defaultSubscribeCtaLabel: CHOOSE_PLAN_SUBSCRIBE_CTA,
      defaultCheckoutLoadingLabel: CHOOSE_PLAN_CHECKOUT_LOADING_LABEL,
    }),
    [
      dualModel,
      premiumTab,
      onPremiumTabChange,
      premiumCard,
      screenData.showMonthlyHint,
      screenData.showListLoading,
      screenData.showEmptyState,
      handleSubscribe,
      checkoutLoadingPlanId,
    ]
  );

  const exitFooter = useMemo(
    () => ({
      loading: exitLoading,
      loadingLabel: CHOOSE_PLAN_EXIT_LOADING_LABEL,
      label: CHOOSE_PLAN_EXIT_FOOTER_LABEL,
      onExit: handleExitToLogin,
    }),
    [exitLoading, handleExitToLogin]
  );

  return useMemo(
    () => ({
      layout: { title: CHOOSE_PLAN_SCREEN_TITLE },
      view,
      exitFooter,
    }),
    [view, exitFooter]
  );
}
